import AccountContext from 'context/AccountContext';
import PriceEstimateContext from 'context/PriceEstimateContext';
import { startOfMinute } from 'date-fns';
import React, { useContext, useEffect } from 'react';
import { useActiveElementStore } from 'stores/activeElementStore';
import { useParkingStore } from 'stores/parkingStore';
import { useParkzoneStore } from 'stores/parkzoneStore';
import { PaymentType } from 'types/generatedSchemaTypes';
import { getCustomerSegmentUid } from 'utils/parkUtils';
import useSelectedCompanyBenefit from './useSelectedCompanyBenefit';

const useFetchPriceEstimate = ({
  controllerRef,
  sessionId,
  parkingZoneUid,
  startTime,
  endTime,
}: {
  controllerRef: React.MutableRefObject<AbortController | undefined>;
  sessionId: string | undefined;
  parkingZoneUid: string;
  startTime: Date | undefined;
  endTime: Date;
}) => {
  const {
    getPricingEstimateWithoutSessionId,
    getPricingEstimateWithSessionId,
  } = useContext(PriceEstimateContext);
  const { customer } = useContext(AccountContext);
  const aimoPlusSubscriber = customer?.aimoPlusSubscriber;

  const { selectedParkingVehiclePlate } = useParkingStore((state) => state);

  const selectedCustomerSegment = useParkzoneStore(
    (state) => state.selectedTariffId
  );
  const customerSegmentUid = getCustomerSegmentUid(selectedCustomerSegment);
  const {
    corporateAccountTransactionFee,
    parkingCorporateAccountDiscountPercent,
  } = useSelectedCompanyBenefit(parkingZoneUid);

  useEffect(() => {
    fetchPricing();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    parkingZoneUid,
    endTime,
    customerSegmentUid,
    corporateAccountTransactionFee,
    parkingCorporateAccountDiscountPercent,
    aimoPlusSubscriber,
  ]);

  const fetchPricing = () => {
    controllerRef.current = new AbortController();
    if (!sessionId) {
      getPricingEstimateWithoutSessionId({
        variables: {
          input: {
            parkingZoneUid,
            startDate: startTime
              ? new Date(
                  Math.max(
                    startTime.getTime(),
                    startOfMinute(new Date()).getTime()
                  )
                )
              : startOfMinute(new Date()),
            endDate: endTime ?? startOfMinute(new Date()),
            customerSegmentUid,
            licensePlate: selectedParkingVehiclePlate,
            ...(corporateAccountTransactionFee != null && {
              serviceFee: corporateAccountTransactionFee,
              paymentType: 'CORPORATE_ACCOUNT' satisfies PaymentType,
            }),
            discountPercent: parkingCorporateAccountDiscountPercent,
          },
        },
        context: { fetchOptions: { signal: controllerRef?.current?.signal } },
        skip:
          !parkingZoneUid ||
          useActiveElementStore.getState().activeElementType === 'CHARGING',
      });
      return;
    }
    getPricingEstimateWithSessionId({
      variables: {
        input: {
          sessionId: sessionId,
          endDate: endTime ?? new Date(),
          serviceFee: corporateAccountTransactionFee,
        },
      },
      context: { fetchOptions: { signal: controllerRef?.current?.signal } },
    });
  };
};

export default useFetchPriceEstimate;
